import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../../services/apiService';
import { environment } from '../../../../environments/environment';
import * as FormBuilderActions from './form-builder.actions';
import { FormBuilderConfig } from '../models/form-builder.models';
import { selectFormBuilderState } from './form-builder.selectors';
import { FormBuilderStorageService } from './form-builder.storage.service';
import {
  transformUtilityAssistance,
  transformSeniorCenterProgram,
  enrichSeniorCenterFormValuesFromConfig,
} from '../transformers/utility-assistance.transformer';
import { SecondSeniorSubmitService } from '../../../services/second-senior-submit.service';
import {
  isSecondSeniorFlowActive,
  isSecondSeniorSpouseRecipient,
} from '../utils/senior-center-registration.mapper';

@Injectable()
export class FormBuilderEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly apiService = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(FormBuilderStorageService);
  private readonly secondSeniorSubmitService = inject(SecondSeniorSubmitService);
  private readonly baseUrl = (environment as any).apiBaseUrl || '';

  // Load Form Configuration Effect
  loadFormConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormBuilderActions.loadFormConfig),
      switchMap(({ programId }) => {
        // Check if this is a static program (1002, 1001, 1000) - load from local JSON instead of API
        const staticProgramIds = ['1002', 'utility-assistance', '1001', 'weatherization-assistance', '1000', 'senior-center-program'];
        const isStaticProgram = staticProgramIds.some(id => programId === id || programId === String(id) || programId.includes(id));
        
        if (isStaticProgram) {
          // Load static program from assets
          let programFileName = programId;
          if (programId === '1002' || programId === String(1002) || programId.includes('utility')) {
            programFileName = 'utility-assistance';
          } else if (programId === '1001' || programId === String(1001) || programId.includes('weatherization')) {
            programFileName = 'weatherization-assistance';
          } else if (programId === '1000' || programId === String(1000) || programId.includes('senior-center')) {
            programFileName = 'senior-center-program';
          }
          const jsonPath = `/assets/program/${programFileName}.json?v=${Date.now()}`;
          
          return this.http.get<FormBuilderConfig>(jsonPath).pipe(
            map((config: FormBuilderConfig) => {
              return FormBuilderActions.loadFormConfigSuccess({ config });
            }),
            catchError((error) => {
              return of(
                FormBuilderActions.loadFormConfigFailure({
                  error: 'Failed to load static program configuration',
                })
              );
            })
          );
        }
        
        // Load from API for non-static programs
        return this.apiService.getProgram(programId).pipe(
          switchMap((response: any) => {
            try {
              // Check for statusCode 2004 (session expired)
              if (response?.statusCode === 2004) {
                // Session expired - the interceptor will handle navigation
                // Return error to prevent form loading - don't fallback to assets
                return throwError(() => ({ error: { statusCode: 2004, message: response.message || 'Session expired' } }));
              }
              
              // Parse formJson from API response
              let config: FormBuilderConfig | null = null;
              
              if (response?.formJson) {
                config = response.formJson;
              } else if (response?.formConfig) {
                config = response.formConfig;
              } else if (response?.data?.formConfig) {
                config = response.data.formConfig;
              } else if (response?.sections) {
                config = response;
              }
              
              if (config?.sections && Array.isArray(config.sections) && config.sections.length > 0) {
                return of(FormBuilderActions.loadFormConfigSuccess({ config }));
              } else {
                // Fallback to local JSON
                return this.loadFormFromAssets();
              }
            } catch (error) {
              // Fallback to local JSON
              return this.loadFormFromAssets();
            }
          }),
          catchError((error) => {
            // Check for statusCode 2004 in error response
            if (error?.error?.statusCode === 2004 || error?.statusCode === 2004) {
              // Session expired - the interceptor will handle navigation
              // Return error to prevent form loading - don't fallback to assets
              return throwError(() => error);
            }
            // Fallback to local JSON only for non-session-expired errors
            return this.loadFormFromAssets();
          })
        );
      })
    )
  );

  // Load from assets fallback
  private loadFormFromAssets() {
    return this.http.get<FormBuilderConfig>('/assets/form-config.json').pipe(
      map((config: FormBuilderConfig) =>
        FormBuilderActions.loadFormConfigSuccess({ config })
      ),
      catchError((error) => {
        return of(
          FormBuilderActions.loadFormConfigFailure({
            error: 'Failed to load form configuration',
          })
        );
      })
    );
  }

  // Submit Form Effect
  submitForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormBuilderActions.submitForm),
      withLatestFrom(this.store.select(selectFormBuilderState)),
      switchMap(([action, state]) => {
        if (!state.config) {
          return of(
            FormBuilderActions.submitFormFailure({
              error: 'Form configuration not loaded',
            })
          );
        }

        // Get program_id from storage
        const programId = this.storageService.loadProgramId();
        if (!programId) {
          return of(
            FormBuilderActions.submitFormFailure({
              error: 'Program ID not found',
            })
          );
        }

        // Check if this is a static program
        const isStatic = state.isStatic === true;
        const staticConfig = state.staticConfig;

        if (isStatic && staticConfig) {
          // Check sessionStorage for rejected / re-upload status and map to 're-submit' for API
          const programStatus = (sessionStorage.getItem('program_status') || '')
            .trim()
            .toLowerCase()
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ');
          const resubmitApplicationId = sessionStorage
            .getItem('senior_center_resubmit_application_id')
            ?.trim();
          const isFullResubmit = sessionStorage.getItem('staticform_full_resubmit') === '1';
          let submissionStatus: 'draft' | 'pending' | 're-submit' = action.status;
          if (
            action.status === 'pending' &&
            (isFullResubmit ||
              !!resubmitApplicationId ||
              programStatus === 're-upload' ||
              programStatus === 're upload' ||
              programStatus === 'rejected' ||
              programStatus === 'returned')
          ) {
            submissionStatus = 're-submit';
          }
          
          // Check if this is a utility assistance program with weatherization
          const weatherizationToggle = state.formValues?.['1084']; // Field 1084 is weatherization toggle
          const hasWeatherizationData = weatherizationToggle === 'yes';
          
          // Transform form values to static program format
          const mainPayload = this.transformStaticFormData(
            state.formValues,
            state.config,
            state.uploadedFilesMap,
            submissionStatus,
            staticConfig,
            programId
          );

          const programIdStr = String(programId);
          const programIdNum = staticConfig.program_id || parseInt(programIdStr, 10) || 0;
          if (resubmitApplicationId) {
            (mainPayload as Record<string, unknown>)['application_id'] = resubmitApplicationId;
          }
          if (programIdNum === 1000 || programIdStr === '1000' || programIdStr.includes('senior-center')) {
            const senior = mainPayload as Record<string, unknown>;
            console.log('[StaticFormApply] Senior Center payload (pre-POST)', {
              status: senior?.['status'],
              recipientsCount: Array.isArray(senior?.['recipients']) ? (senior['recipients'] as unknown[]).length : 0,
              healthPlansCount: Array.isArray(senior?.['health_plans']) ? (senior['health_plans'] as unknown[]).length : 0,
              nutritionalAssessment: senior?.['nutritional_assessment'],
              firstRecipient: Array.isArray(senior?.['recipients']) ? (senior['recipients'] as unknown[])[0] : null,
            });
            console.log('[StaticFormApply] Senior Center full payload JSON', JSON.stringify(mainPayload));
          }

          const programIdStrForGate = String(programId);
          const isSeniorCenterProgram =
            programIdStrForGate === '1000' ||
            programIdStrForGate.includes('senior-center') ||
            Number(staticConfig.program_id) === 1000;
          const isSecondSeniorEnrollment =
            isSeniorCenterProgram &&
            (isSecondSeniorFlowActive() || isSecondSeniorSpouseRecipient(mainPayload as Record<string, unknown>));

          if (isSecondSeniorEnrollment) {
            return this.secondSeniorSubmitService
              .submitSecondSeniorApplication(
                mainPayload as Record<string, unknown>,
                submissionStatus
              )
              .pipe(
                map((response) => {
                  sessionStorage.removeItem('senior_center_resubmit_application_id');
                  sessionStorage.removeItem('staticform_full_resubmit');
                  return FormBuilderActions.submitFormSuccess({ response });
                }),
                catchError((error) => {
                  let errorMessage = 'An error occurred while submitting the form';
                  if (error?.error?.message) {
                    errorMessage = error.error.message;
                  } else if (error?.message) {
                    errorMessage = error.message;
                  } else if (typeof error === 'string') {
                    errorMessage = error;
                  }
                  return of(
                    FormBuilderActions.submitFormFailure({
                      error: {
                        message: errorMessage,
                        originalError: error,
                      },
                    })
                  );
                })
              );
          }

          // Use custom endpoint if provided, otherwise use default
          const endpoint = staticConfig.api_endpoint || '/api/program/staticform/apply';
          
          // Submit main form (utility assistance)
          return this.http.post(`${this.baseUrl}${endpoint}`, mainPayload).pipe(
            switchMap((mainResponse) => {
              sessionStorage.removeItem('senior_center_resubmit_application_id');
              sessionStorage.removeItem('staticform_full_resubmit');

              // Only co-submit weatherization API if:
              // 1. Program is 1002 (utility assistance)
              // 2. Weatherization is selected (toggle is 'yes')
              // 3. Status is 'pending' or 're-submit' (not 'draft')
              // For 'draft' status, weatherization_details is already included in the main payload (program_id 1002)
              // Standalone Weatherization (1001) submits only itself — do not auto-create Utility.
              const programIdStr = String(programId);
              const isUtilityProgram = programIdStr === '1002';
              const isSubmittedStatus = action.status === 'pending' || submissionStatus === 're-submit';

              if (isUtilityProgram && hasWeatherizationData && isSubmittedStatus) {
                
                // Create weatherization payload with utility data + weatherization fields
                // Map field IDs to field names (matching the API expected format)
                const weatherizationData: Record<string, any> = {
                  is_applying: true // Set to true since we're only here if weatherization toggle is 'yes'
                };
                
                // Add is_apply_weatherization (bool?, nullable) from field 1084
                const field1084Value = state.formValues['1084'];
                if (field1084Value !== undefined && field1084Value !== null && field1084Value !== '') {
                  weatherizationData['is_apply_weatherization'] = field1084Value === 'yes' || field1084Value === 'Yes' ? true : 
                                                                  field1084Value === 'no' || field1084Value === 'No' ? false : null;
                } else {
                  weatherizationData['is_apply_weatherization'] = null;
                }
                
                // Add is_referred (bool?, nullable) from field 1111
                const field1111Value = state.formValues['1111'];
                if (field1111Value !== undefined && field1111Value !== null && field1111Value !== '') {
                  weatherizationData['is_referred'] = field1111Value === 'yes' || field1111Value === 'Yes' ? true : 
                                                       field1111Value === 'no' || field1111Value === 'No' ? false : null;
                } else {
                  weatherizationData['is_referred'] = null;
                }
                
                // Map field IDs to field names (matching the API expected format)
                const fieldIdToNameMap: Record<string, string> = {
                  '1027': 'is_home_drafty', // Is your Home Drafty?
                  '1028': 'received_new_attic_insulation', // Attic insulation
                  '1029': 'installed_led_bulbs', // LED bulbs
                  '1030': 'walls_have_insulation', // Walls insulation
                  '1031': 'refrigerator_age', // Refrigerator age
                  '1032': 'currently_remodeling_home', // Remodeling
                  '1033': 'accept_gas_stove_replacement', // Gas stove
                  '1034': 'ac_age', // AC age
                  '1035': 'is_ac_working', // AC working
                  '1036': 'heater_age', // Heater age
                  '1037': 'is_heat_working', // Heat working
                  '1038': 'is_electric_or_gas', // Electric or gas
                  '1039': 'has_central_heat_and_air', // Central heat
                  '1040': 'accept_replace_unvented_gas_heaters', // Unvented heater
                  '1041': 'has_solar_screens_on_windows', // Solar screens
                  '1042': 'has_structural_problem', // Structural problem
                  '1043': 'has_large_exposure_to_outdoors', // Outdoor exposure
                  '1044': 'has_roof_leaks', // Roof leaks
                  '1045': 'has_moisture_or_mold_problem', // Moisture/mold
                  '1120': 'where_moisture_or_mold_problems', // Moisture/mold locations
                  '1046': 'has_bugs_rodents_beehives', // Infestation
                  '1047': 'has_unusual_odors_smells', // Odors
                  '1048': 'has_headache_itchy_eyes_sinus_dizziness', // Health symptoms
                  '1049': 'risk_family_health_safety', // Safety risks
                  '1050': 'hear_about_weatherization_program' // How heard about program
                };
                
                // Convert field IDs to field names (skip 1084 and 1111 as they're already handled above)
                Object.keys(fieldIdToNameMap).forEach(fieldId => {
                  const fieldName = fieldIdToNameMap[fieldId];
                  if (state.formValues[fieldId] !== undefined) {
                    weatherizationData[fieldName] = state.formValues[fieldId];
                  }
                });
                
                const weatherizationPayload = {
                  ...mainPayload,
                  program_id: 1001, // Change program ID to 1001 for weatherization
                  weatherization_details: weatherizationData
                };
                
                // Submit weatherization data to form 1001
                return this.http.post(`${this.baseUrl}/api/program/staticform/apply`, weatherizationPayload).pipe(
                  map((weatherizationResponse) => {
                    // Return both responses
                    return FormBuilderActions.submitFormSuccess({ 
                      response: { mainResponse, weatherizationResponse }
                    });
                  }),
                  catchError((error) => {
                    // Even if weatherization fails, the main form was submitted
                    // Return the main response with weatherization error
                    return of(
                      FormBuilderActions.submitFormSuccess({ 
                        response: mainResponse
                      })
                    );
                  })
                );
              } else {
                // No weatherization submission - either not utility program, no weatherization data, or draft status
                if (!isUtilityProgram) {
                  console.log('[FormBuilderEffects] Skipping weatherization API - not utility program (programId:', programId, ')');
                } else if (!hasWeatherizationData) {
                  console.log('[FormBuilderEffects] Skipping weatherization API - weatherization not selected');
                } else if (action.status !== 'pending') {
                  console.log('[FormBuilderEffects] Skipping weatherization API - status is draft, weatherization_details included in main payload');
                }
                
                // Just return main response
                return of(
                  FormBuilderActions.submitFormSuccess({ response: mainResponse })
                );
              }
            }),
            catchError((error) => {
              sessionStorage.removeItem('senior_center_resubmit_application_id');
              sessionStorage.removeItem('staticform_full_resubmit');
              // Extract error message from error response
              let errorMessage = 'An error occurred while submitting the form';
              
              if (error?.error?.message) {
                errorMessage = error.error.message;
              } else if (error?.message) {
                errorMessage = error.message;
              } else if (typeof error === 'string') {
                errorMessage = error;
              }
              
              return of(
                FormBuilderActions.submitFormFailure({ 
                  error: {
                    message: errorMessage,
                    originalError: error
                  }
                })
              );
            })
          );
        } else {
          // Existing dynamic program logic
          // Create a deep copy of the config to update field values
          const updatedConfig: FormBuilderConfig = JSON.parse(JSON.stringify(state.config));
          
          // Update all field values in the config with user-entered values
          if (updatedConfig.sections) {
            updatedConfig.sections = updatedConfig.sections.map(section => ({
              ...section,
              groups: section.groups?.map(group => {
                // Update table_data for table groups
                if (group.is_table) {
                  const tableKey = `table_${group.group_id}`;
                  const tableData = state.formValues[tableKey];
                  if (tableData !== undefined && tableData !== null) {
                    return { ...group, table_data: tableData };
                  }
                  return group;
                }
                
                // Update field values for regular groups
                if (group.fields) {
                  return {
                    ...group,
                    fields: group.fields.map(field => {
                      const fieldValue = state.formValues[field.field_id];
                      // Update the value property if it exists in formValues
                      if (fieldValue !== undefined) {
                        return { ...field, value: fieldValue };
                      }
                      return field;
                    })
                  };
                }
                
                return group;
              })
            }));
          }

          // Prepare payload with program_id, status, and form_json
          const payload = {
            program_id: programId,
            status: action.status, // 'draft' or 'pending'
            form_json: updatedConfig
          };

          // Send payload with program_id, status, and form_json
          return this.apiService.applyProgram(payload).pipe(
            map((response) =>
              FormBuilderActions.submitFormSuccess({ response })
            ),
            catchError((error) => {
              // Extract error message from error response
              // Error structure: { statusCode: 4011, message: "User has already applied for this program", error: null }
              let errorMessage = 'An error occurred while submitting the form';
              
              if (error?.error?.message) {
                errorMessage = error.error.message;
              } else if (error?.message) {
                errorMessage = error.message;
              } else if (typeof error === 'string') {
                errorMessage = error;
              }
              
              return of(
                FormBuilderActions.submitFormFailure({ 
                  error: {
                    message: errorMessage,
                    originalError: error
                  }
                })
              );
            })
          );
        }
      })
    )
  );

  // Save form values to sessionStorage whenever they change (simple - no loops)
  saveFormValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FormBuilderActions.updateFormValue,
        FormBuilderActions.updateFormValues,
        FormBuilderActions.updateTableData,
        FormBuilderActions.updateCurrentSection
      ),
      withLatestFrom(this.store.select(selectFormBuilderState)),
      tap(([action, state]) => {
        // Save form values
        this.storageService.saveFormValues(state.formValues);
        // Also save uploaded files map
        this.storageService.saveUploadedFilesMap(state.uploadedFilesMap);
        // Save current section ID
        if (state.currentSectionId) {
          this.storageService.saveCurrentSection(state.currentSectionId);
        }
      })
    ),
    { dispatch: false }
  );

  // Save uploaded files map to sessionStorage whenever it changes
  saveUploadedFilesMap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormBuilderActions.updateUploadedFilesMap),
      withLatestFrom(this.store.select(selectFormBuilderState)),
      tap(([action, state]) => {
        // Save uploaded files map
        this.storageService.saveUploadedFilesMap(state.uploadedFilesMap);
      })
    ),
    { dispatch: false }
  );

  // Clear persisted data after successful form submission
  clearPersistedDataOnSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormBuilderActions.submitFormSuccess),
      tap(() => {
        // Clear all persisted data after successful submission
        this.storageService.clearAll();
      })
    ),
    { dispatch: false }
  );

  /**
   * Create weatherization payload for form 1001
   * Extracts only weatherization-related fields (1027-1050, 1120, 1084) from the form
   */
  private createWeatherizationPayload(
    formValues: Record<string, any>,
    config: FormBuilderConfig | null,
    uploadedFilesMap: Record<string, any[]>,
    status: 'draft' | 'pending'
  ): any {
    // Weatherization field IDs: toggle 1084, content fields 1027-1050, and 1120 (moisture/mold locations)
    const weatherizationFieldIds = [
      '1084', // Toggle
      '1027', '1028', '1029', '1030', '1031', '1032', '1033', // Interior & Appliances
      '1034', '1035', '1036', '1037', '1038', '1039', '1040', // A/C & Heat
      '1041', '1042', '1043', '1044', // Exterior & Structure
      '1045', '1120', '1046', '1047', '1048', '1049', '1050' // Household & Safety
    ];

    // Extract only weatherization fields
    const weatherizationData: Record<string, any> = {};
    weatherizationFieldIds.forEach(fieldId => {
      if (formValues[fieldId] !== undefined) {
        weatherizationData[fieldId] = formValues[fieldId];
      }
    });

    // Add metadata
    const payload = {
      ...weatherizationData,
      status,
      program_id: 1001, // Weatherization form ID
      original_program_id: 1002 // Reference to the original utility assistance program
    };

    return payload;
  }

  /**
   * Transform static form data using the configured transformer function
   */
  private transformStaticFormData(
    formValues: Record<string, any>,
    config: FormBuilderConfig | null,
    uploadedFilesMap: Record<string, any[]>,
    status: 'draft' | 'pending' | 're-submit',
    staticConfig: any,
    programId: string
  ): any {
    // Check if there's a custom transform function
    const transformFunctionName = staticConfig.transform_function;
    const programIdNum = staticConfig.program_id || parseInt(programId) || 1002;
    
    // For Senior Center Program (1000), use transformSeniorCenterProgram
    if (programIdNum === 1000 || programId === '1000' || programId.includes('senior-center')) {
      const mergedFormValues = enrichSeniorCenterFormValuesFromConfig(formValues, config);
      const payload = transformSeniorCenterProgram(
        mergedFormValues,
        config,
        uploadedFilesMap,
        programIdNum,
        status
      );
      return payload;
    }
    
    if (transformFunctionName === 'transformUtilityAssistance') {
      const payload = transformUtilityAssistance(
        formValues,
        config,
        uploadedFilesMap,
        programIdNum,
        status
      );
      return payload;
    }
    
    // Default: send form values as-is
    return {
      ...formValues,
      status,
      program_id: staticConfig.program_id || parseInt(programId) || 0
    };
  }
}

